import { Injectable, EventEmitter, Output } from '@angular/core';
import { Observable } from 'rxjs';

import { QuoteService } from '../services/quote.service';
import { OptionsService } from '../services/options.service';
import { CurrentUserService } from '../services/current-user.service';
import { LoadingSpinnerService } from '../services/loading-spinner.service';
import { AuthenticatedHttpService } from './authenticatedhttp.service';
import { QuoteRevisionService } from '../services/quote-revision.service';

import { MchApplicationState } from '../model/application-state';
import { Quote } from '../model/quote';
import { ProductLine } from '../model/product-line';
import { Model } from '../model/Model';
import { Customer } from '../model/customer';
import { User } from '../model/user';
import { Prospect } from '../model/prospect';
import { QuoteRevision } from '../model/quote-revision';

@Injectable()
export class ApplicationStateService {
  private mchApplicationState: MchApplicationState = new MchApplicationState();
  chosenModel: Model;
  chosenProductLine: ProductLine;
  currentUser: User = undefined;
  cmhUser: any = undefined;

  pricingViewChange: EventEmitter<number> = new EventEmitter();

  constructor(
    private quoteService: QuoteService,
    private optionsService: OptionsService,
    private currentUserService: CurrentUserService,
    private spinner: LoadingSpinnerService,
    private _http: AuthenticatedHttpService,
    private quoteRevisionService: QuoteRevisionService
  ) {
    this.mchApplicationState.quote = undefined;
    this.mchApplicationState.productLine = undefined;
    this.mchApplicationState.model = undefined;
    this.mchApplicationState.customer = undefined;

    this.mchApplicationState.quoteId = undefined;
    this.mchApplicationState.productLineId = undefined;
    this.mchApplicationState.modelId = undefined;
    this.mchApplicationState.customerId = undefined;
    this.mchApplicationState.categoryId = undefined;
    this.mchApplicationState.userId = 1;
    this.mchApplicationState.userName = 'Guest';
    this.mchApplicationState.pricingView = 2;
    this.mchApplicationState.prospectId = undefined;
  }

  @Output() updatePricingView = new EventEmitter<number>();

  getApplicationState() {
    return this.mchApplicationState;
  }

  setProductLine(
    productLine: ProductLine,
    refreshOptions: boolean = false
  ): Observable<Quote> {
    if (this.mchApplicationState.quote === undefined) {
      this.mchApplicationState.quote = new Quote();
    }
    let quote = this.mchApplicationState.quote;
    if (refreshOptions) {
      if (quote.modifiedBy === null) {
        this.currentUserService.currentUser$.subscribe(x => {
          quote.modifiedBy = x.cmhUserId + '-' + x.fullName;
        });
      }
      this.optionsService
        .deleteSelectedOptions(quote.quoteId, quote.modifiedBy)
        .subscribe();
    }
    if (quote.productLineId !== productLine.ProductLineId) {
      this.mchApplicationState.quote.DateApproved = null;
      this.mchApplicationState.quote.DateCreated = new Date();
      this.mchApplicationState.quote.LastModifiedDate = null;
      this.mchApplicationState.quote.modelId = null;
    }
    quote.productLineId = productLine.ProductLineId;
    quote.plantId = productLine.PlantId;
    this.mchApplicationState.categoryId = undefined;
    return this.saveState();
  }

  setModel(model: Model, refreshOptions: boolean = false): Observable<Quote> {
    let quote = this.mchApplicationState.quote;
    if (refreshOptions) {
      if (quote.modifiedBy === null) {
        this.currentUserService.currentUser$.subscribe(x => {
          quote.modifiedBy = x.cmhUserId + '-' + x.fullName;
        });
      }
      this.optionsService
        .deleteSelectedOptions(quote.quoteId, quote.modifiedBy)
        .subscribe();
    }
    if (quote.modelId !== model.ModelId) {
      this.mchApplicationState.quote.DateApproved = null;
      this.mchApplicationState.quote.DateCreated = new Date();
      this.mchApplicationState.quote.LastModifiedDate = null;
    }
    quote.modelId = model.ModelId;
    quote.BaseCost = model.BasePrice;
    quote.ModelBaseCost = model.BasePrice;
    this.mchApplicationState.categoryId = undefined;
    return this.saveState();
  }

  getCurrentUser() {
    return this.currentUser;
  }

  setCategoryId(categoryId: number) {
    this.mchApplicationState.categoryId = categoryId;
  }

  setQuote(quote: Quote) {
    if (quote.DateCreated === undefined) {
      quote.DateCreated = new Date();
    }
    this.mchApplicationState.quote = quote;
    this.mchApplicationState.quoteId = quote.quoteId;
    this.mchApplicationState.productLineId = quote.productLineId;
    if (quote.modelId !== null && quote.modelId !== undefined) {
      this.mchApplicationState.modelId = quote.modelId;
    }
    this.mchApplicationState.customerId = quote.customerId;
    this.mchApplicationState.retailerNumber = quote.retailerNumber;
    this.mchApplicationState.plantId = quote.plantId;
    this.mchApplicationState.prospectId = quote.prospectId;
    this.mchApplicationState.quote.approvalStatus = quote.QuoteStatusesID;
    this.mchApplicationState.quote.QuoteStatusesID = quote.QuoteStatusesID;
  }

  getQuote() {
    if (this.mchApplicationState.quote === undefined)
      this.mchApplicationState.quote = new Quote();
    return this.mchApplicationState.quote;
  }

  setQuoteStatus(
    status: number,
    note: string = null,
    expirationdate: Date = null
  ): Observable<Quote> {
    this.mchApplicationState.quote.approvalStatus = status;
    this.mchApplicationState.quote.approvalNote = note;
    this.mchApplicationState.quote.expirationDate = expirationdate;

    return this.saveState();
  }

  setQuotePricing(
    baseCost: number,
    baseCostOverride: number,
    dues: number,
    optionsTotal: number,
    partnerAssistanceAmount: number,
    vipDiscountAmount:number,
    surcharge: number,
    taxes: number,
    transportation: number,
    discountPercent: number
  ) {
    this.mchApplicationState.quote.BaseCost =
      baseCostOverride > 0 ? baseCostOverride : baseCost;
    this.mchApplicationState.quote.Dues = dues;
    this.mchApplicationState.quote.OptionsTotal = optionsTotal;
    this.mchApplicationState.quote.VipDiscountAmount = vipDiscountAmount;
    this.mchApplicationState.quote.PartnerAssistanceAmount = partnerAssistanceAmount;
    this.mchApplicationState.quote.Surcharge = surcharge;
    this.mchApplicationState.quote.Taxes = taxes;
    this.mchApplicationState.quote.Transportation = transportation;
    this.mchApplicationState.quote.GrandTotal =
      this.mchApplicationState.quote.BaseCost +
      optionsTotal +
      dues +
      transportation;
  }

  setQuotePricingAndUpdate(
    baseCost: number,
    baseCostOverride: number,
    dues: number,
    optionsTotal: number,
    vipDiscountAmount: number,
    partnerAssistAmount: number,
    surcharge: number,
    taxes: number,
    transportation: number,
    estimatedTotal: number
  ): Observable<Quote> {
    this.mchApplicationState.quote.BaseCost =
      baseCostOverride > 0 ? baseCostOverride : baseCost;
    this.mchApplicationState.quote.Dues = dues;
    this.mchApplicationState.quote.OptionsTotal = optionsTotal;
    this.mchApplicationState.quote.VipDiscountAmount = vipDiscountAmount;
    this.mchApplicationState.quote.PartnerAssistanceAmount = partnerAssistAmount;
    this.mchApplicationState.quote.Surcharge = surcharge;
    this.mchApplicationState.quote.Taxes = taxes;
    this.mchApplicationState.quote.Transportation = transportation;
    this.mchApplicationState.quote.GrandTotal =
      this.mchApplicationState.quote.BaseCost +
      optionsTotal +
      dues +
      transportation;

    return this.saveState();
  }

  private saveState(): Observable<Quote> {
    this.spinner.showSpinner();
    let quote = this.mchApplicationState.quote;
    if (this.cmhUser !== undefined) {
      quote.modifiedBy =
        this.cmhUser.cmhUserId +
        '-' +
        this.cmhUser.firstName +
        ' ' +
        this.cmhUser.lastName;
    }
    if (this.mchApplicationState.quote.revisionNumber === null) {
      this.mchApplicationState.quote.revisionNumber = 1;
      this.mchApplicationState.quote.revisionComments = 'Quote Created';
    }
    if (!quote || quote.quoteId === 0) {
      return this.quoteService
        .insertQuote(this.mchApplicationState.quote)
        .map(q => {
          q.modifiedBy = quote.modifiedBy;
          this.setQuote(q);

          // now create initial quote revision
          let quoteRevision = new QuoteRevision();
          quoteRevision.RevisionNumber = 1;
          quoteRevision.RevisionComments = 'Quote Created';
          quoteRevision.QuoteId = q.quoteId;

          this.quoteRevisionService
            .insertQuoteRevision(quoteRevision)
            .subscribe(
              quoteRevision => {},
              error => {
                this._http.handleError;
              }
            );

          return q;
        })
        .catch(this._http.handleError);
    } else {
      return this.quoteService
        .updateQuote(this.mchApplicationState.quote)
        .map(q => {
          q.modifiedBy = quote.modifiedBy;
          this.setQuote(q);
          return q;
        })
        .catch(this._http.handleError);
    }
  }

  refreshStateFromDB(quoteId, refresh = true): Observable<Quote> {
    return this.quoteService.getQuoteById(quoteId, refresh);
  }

  setPricingView(pricingview: number) {
    switch (pricingview) {
      case 1:
      case 2:
      case 3:
        this.mchApplicationState.pricingView = pricingview;
        break;
      default:
        this.mchApplicationState.pricingView = 3;
        break;
    }
    //this.emitPricingViewEvent(pricingview);
  }

  get pricingView(): number {
    return this.mchApplicationState.pricingView;
  }

  setCustomer(customer: Customer): Observable<Quote> {
    if (this.mchApplicationState.quote === undefined) {
      this.mchApplicationState.quote = new Quote();
    }
    let retailer;
    this.currentUserService.currentUser$.subscribe(x => {
      retailer = x;
    });
    this.mchApplicationState.customer = {
      customerId: customer.customerId,
      firstName: customer.firstName,
      lastName: customer.lastName,
      address: customer.address,
      city: customer.city,
      email: customer.email,
      phone: customer.phone,
      state: customer.state,
      zip: customer.zip,
      modifiedBy: customer.modifiedBy,
      quoteRevisionNumber: customer.quoteRevisionNumber
    };
    let quote = this.mchApplicationState.quote;
    quote.customerId = customer.customerId;
    quote.retailerNumber = retailer.retailerNumber;
    quote.prospectId = null;
    return this.saveState().map(q => {
      return q;
    });
  }

  setProspect(prospect: Prospect, modifiedBy: string): Observable<Quote> {
    if (this.mchApplicationState.quote === undefined) {
		this.mchApplicationState.quote = new Quote();
    }
    let retailer;
    this.currentUserService.currentUser$.subscribe(x => {
      retailer = x;
    });
    this.mchApplicationState.customer = {
      customerId: null,
      firstName:
        prospect.Contact === undefined ? 'Stock' : prospect.Contact.FirstName,
      lastName:
        prospect.Contact === undefined ? 'Home' : prospect.Contact.LastName,
      address:
        prospect.Contact !== undefined &&
        prospect.Contact.ContactAddresses !== undefined &&
        prospect.Contact.ContactAddresses.length > 0
          ? prospect.Contact.ContactAddresses[0].Address1
          : null,
      city:
        prospect.Contact !== undefined &&
        prospect.Contact.ContactAddresses !== undefined &&
        prospect.Contact.ContactAddresses.length > 0
          ? prospect.Contact.ContactAddresses[0].City
          : null,
      email:
        prospect.Contact !== undefined &&
        prospect.Contact.ContactEmails !== undefined &&
        prospect.Contact.ContactEmails.length > 0
          ? prospect.Contact.ContactEmails[0].EmailAddress
          : null,
      phone:
        prospect.Contact !== undefined &&
        prospect.Contact.ContactPhoneNumbers !== undefined &&
        prospect.Contact.ContactPhoneNumbers.length > 0
          ? prospect.Contact.ContactPhoneNumbers[0].PhoneNumber
          : null,
      state:
        prospect.Contact !== undefined &&
        prospect.Contact.ContactAddresses !== undefined &&
        prospect.Contact.ContactAddresses.length > 0
          ? prospect.Contact.ContactAddresses[0].State
          : null,
      zip:
        prospect.Contact !== undefined &&
        prospect.Contact.ContactAddresses !== undefined &&
        prospect.Contact.ContactAddresses.length > 0
          ? prospect.Contact.ContactAddresses[0].Zip
          : null,
		modifiedBy: modifiedBy,
		quoteRevisionNumber: (this.mchApplicationState.quote.revisionNumber === null || this.mchApplicationState.quote.revisionNumber === undefined) ? 1 : this.mchApplicationState.quote.revisionNumber
    };
    let quote = this.mchApplicationState.quote;
    quote.customerId = null;
    quote.retailerNumber = retailer.retailerNumber;
    quote.prospectId = prospect.ProspectId;
    return this.saveState().map(q => {
      return q;
    });
  }

  setCmhUser(cmhUser: any) {
    this.cmhUser = cmhUser;
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/services/application-state.service.ts